import { apiErrorText } from "../../../api/api-error.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `live` — the `board` variant (product decision A5; the
 * `feed` variant is registered as not built in `docs/design/screen-manifest.json`).
 *
 * TWO STREAMS, DELIBERATELY SEPARATE. `GET /v1/stream/agent/{voice_agent_id}/live` carries
 * the *board*: conversations starting, updating and ending, and the channel-unit meter
 * moving. `GET /v1/stream/conversation/{conversation_id}/transcript` carries one
 * conversation's turns. Design §5.3 keeps them apart because the board is a view of the
 * account and the transcript is a view of one call; merging them would mean every open
 * console received every turn of every conversation in the workspace.
 *
 * The audio loop never comes through either of them. What arrives here is text.
 *
 * `resync` is honoured rather than ignored: the server sends it instead of dropping events,
 * and a Live view that has silently missed events is a Live view that lies.
 *
 * AMENDED 2026-10-09 (design sync): the meter reads "Calls in progress · 3 of 10" with "7 lines
 * free." under it, and "Request more lines" opens the concurrency request in place
 * (`QuotaRequestDialog`) rather than leading to Billing. The transcript's head draws the voice bars
 * and an ⓘ button that shows the conversation's technical details (its medium and id) on demand.
 *
 * AMENDED 2026-10-09 (plan-015 LIVE, G14, design §28.5.14): for a voice conversation the ⓘ shows what the prototype
 * draws there — the codec and sample rate, the latency so far, and the id ("G.722 · 16 kHz · 640 ms · …") — from the
 * live board's own fields, or "Codec not reported" when the leg reported none. No codec is the console's own.
 *
 * AMENDED 2026-10-19 (plan-021 WAPP-HIST, design §33.13.3, OD-16): the board lists a conversation this VoiceAgent
 * entered or is answering now. One it entered that another VoiceAgent of the workspace is answering after a pass says so
 * on its row — "Now with Delivery" — and the line meter counts only the conversations this VoiceAgent is answering (the
 * api's `channel_unit_in_use`). A conversation that was never passed reads exactly as before.
 *
 * AMENDED 2026-10-28 (plan-029 WAPP-HIST1, design §40.13.1): the transcript offers "Block this number" where the
 * conversation's `may_block` is true, and once a conversation ends, says when caller protection ended it ("Ended: the
 * longest conversation was reached"). A refused caller never reaches the board: it is refused before admission.
 *
 * AMENDED 2026-10-29 (plan-029 WAPP-HIST2, design §40.13.1, §40.13.2): the transcript says caller confirmation's state —
 * "Waiting for the code", "Caller confirmed", "Could not confirm the caller" — from the stored record and the
 * stream's `party_confirmation` events. Never the code.
 *
 * AMENDED 2026-10-28 (WAPP-ENDCALL, owner request): a row that is an OUTBOUND conversation (`outbound_origin` set — a
 * campaign call, an on-demand call) carries "End call" for a person who may dial (`./end-outbound-conversation.tsx`); an inbound row never
 * does. A conversation ended here leaves the list at once, ahead of the next read.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Icon } from "@wetalk/ui";
import { useEffect, useState, type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { PendingQuotaRequestLine, QuotaRequestDialog } from "../../billing/quota-request-dialog.js";
import { useOrganizationConcurrency } from "../../finance/allowances/organization-allowance.js";
import { useGateway } from "../../../api/provider.js";
import { languageNameOf, partyLabel, priceUnavailableNoteOf } from "../../../api/conversation-view.js";
import { LanguageBetaTag, isBetaLanguage } from "../../../shell/language-beta.js";
import { QUERY_KEY, languagePackQuery, liveSnapshotQuery } from "../../../api/query.js";
import { readRecord, readText, readWholeNumber } from "../../../api/decode.js";
import { nowWithTextOf } from "../../../api/voice-agent-stint-view.js";
import { formatDuration, formatMoney } from "../../../money.js";
import { talkTimeText } from "../../../talk-time.js";
import { meterPercent, meterStyle } from "../../../meter.js";
import type { ChannelUnitMeterView, LiveBoardConversationView, Medium, TranscriptLineView } from "../../../api/gateway.js";
import {
  isPartyProtectionEndReason,
  partyProtectionReasonText,
  type PartyProtectionEndReason,
} from "../../../api/party-protection-view.js";
import { BlockThisNumber } from "./block-number-dialog.js";
import {
  liveConfirmationStateAfter,
  liveConfirmationText,
  partyConfirmationFrameDetailOf,
  type LiveConfirmationState,
} from "../../../api/party-confirmation-view.js";
import { EndOutboundConversation } from "./end-outbound-conversation.js";
import { useMemberCan } from "../../../shell/member-capability.js";

const MEDIUM_GLYPH = {
  voice: GLYPH.phoneCall,
  web_chat: GLYPH.browser,
  whatsapp: GLYPH.whatsappLogo,
  viber: GLYPH.chatCircleDots,
} as const satisfies Record<Medium, unknown>;

export interface LiveTabProps {
  readonly voiceAgentId: string;
  readonly selectedConversationId: string;
  readonly onSelect: (conversationId: string) => void;
  /** A survey agent: the far end of a call is "Person called" (design 2026-10-15), not "Caller". */
  readonly survey?: boolean;
}

export function LiveTab({
  voiceAgentId,
  selectedConversationId,
  onSelect,
  survey = false,
}: LiveTabProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const snapshot = useQuery(liveSnapshotQuery(gateway, voiceAgentId));
  const languagePack = useQuery(languagePackQuery(gateway));

  const [meterOverride, setMeterOverride] = useState<ChannelUnitMeterView | null>(null);
  const [stale, setStale] = useState(false);
  const [quotaRequestOpen, setQuotaRequestOpen] = useState(false);
  const organizationConcurrency = useOrganizationConcurrency();
  // WAPP-ENDCALL: "End call" is offered to a person who may dial; a conversation ended here leaves the list at once.
  const mayDial = useMemberCan().can("conversation.dial");
  const [endedConversationId, setEndedConversationId] = useState<ReadonlySet<string>>(new Set());
  const markEnded = (conversationId: string): void => {
    setEndedConversationId((current) => new Set([...current, conversationId]));
  };

  useEffect(() => {
    const subscription = gateway.streamVoiceAgentLive(voiceAgentId, (frame) => {
      switch (frame.event) {
        case "conversation.started":
        case "conversation.ended":
        case "conversation.updated": {
          void queryClient.invalidateQueries({
            queryKey: [...QUERY_KEY.liveSnapshot, voiceAgentId],
          });
          break;
        }
        case "channel_unit.changed": {
          const data = readRecord(frame.data, "channel_unit.changed");
          setMeterOverride((current) => ({
            inUse: readWholeNumber(data["in_use"], "channel_unit.changed.in_use"),
            ceiling: readWholeNumber(data["ceiling"], "channel_unit.changed.ceiling"),
            note: current?.note ?? "",
          }));
          break;
        }
        case "resync": {
          setStale(true);
          break;
        }
        default:
          break;
      }
    });
    return () => {
      subscription.close();
    };
  }, [gateway, queryClient, voiceAgentId]);

  if (snapshot.data === undefined) {
    // Customer-portal review #110: an agent with no live version answers 404 here, and the tab
    // said "Reading…" forever. A refusal is said, in words; the Agent screen no longer offers this
    // tab without a live version, so this is what a link to it or a lost connection shows.
    if (snapshot.isError) {
      const status = snapshot.error instanceof ApiError ? snapshot.error.status : null;
      return (
        <p className="c-note c-note-critical">
          {status === 404
            ? portalText("operations", "nothing_is_happening_here_this_agent_has_no_live_version_y_7defddcd")
            : portalText("operations", "what_is_happening_right_now_could_not_be_read_value_reload_283be79b", { v0: String(apiErrorText(snapshot.error, "")) })}
        </p>
      );
    }
    return <p className="c-note">{portalText("operations", "reading_what_is_happening_right_now_60347263")}</p>;
  }

  const meter: ChannelUnitMeterView =
    meterOverride === null
      ? snapshot.data.meter
      : { ...meterOverride, note: snapshot.data.meter.note };

  const listed = snapshot.data.conversation.filter((one) => !endedConversationId.has(one.conversationId));
  const selected = listed.find((one) => one.conversationId === selectedConversationId) ?? listed[0];

  const room = meter.ceiling - meter.inUse;
  const languageName = (code: string | null): string =>
    code === null ? "—" : (languagePack.data?.find((one) => one.code === code)?.name ?? languageNameOf(code));

  return (
    <>
      <div className="c-va-figures">
        <div>
          <span className="c-va-figure-value">{String(snapshot.data.liveConversationCount)}</span>
          <span className="c-va-figure-label">{portalText("operations", "live_conversations_cca62c52")}</span>
        </div>
        <div>
          <span className="c-va-figure-value">{talkTimeText(snapshot.data.talkSecondToday)}</span>
          <span className="c-va-figure-label">{portalText("operations", "minutes_today_ace9e30c")}</span>
        </div>
        <div>
          <span className="c-va-figure-value">{formatMoney(snapshot.data.spendToday)}</span>
          <span className="c-va-figure-label">{portalText("operations", "spent_today_6a6d69f5")}</span>
          {snapshot.data.priceUnavailableCountToday === 0 ? null : (
            <span className="c-va-figure-label">{priceUnavailableNoteOf(snapshot.data.priceUnavailableCountToday)}</span>
          )}
        </div>
        <div>
          <span className="c-va-figure-value">{String(snapshot.data.outcomeCountToday)}</span>
          <span className="c-va-figure-label">{snapshot.data.outcomeLabel} {portalText("operations", "today_2dd2bed7")}</span>
        </div>
      </div>

      {stale ? (
        <p className="c-note c-note-critical">
          {portalText("operations", "the_live_stream_fell_behind_and_closed_rather_than_skippin_fada1f4a")}</p>
      ) : null}

      <div className="c-va-split">
        <div>
          <div className="c-va-card c-va-meter">
            <div className="c-va-meter-head">
              <span>{portalText("operations", "calls_in_progress_cbb4097c")}</span>
              <span className="c-va-meter-value">
                {String(meter.inUse)} {portalText("operations", "of_2449d652")}{String(meter.ceiling)}
              </span>
            </div>
            <div
              className="c-va-meter-track"
              role="meter"
              aria-valuenow={meter.inUse}
              aria-valuemin={0}
              aria-valuemax={meter.ceiling}
              aria-label={portalText("operations", "lines_in_use_e4a8d799")}
            >
              <div
                className="c-va-meter-fill"
                style={meterStyle(meterPercent(meter.inUse, meter.ceiling))}
              />
            </div>
            <div className="c-va-meter-foot">
              <span className="c-va-meter-note" title={meter.note === "" ? undefined : meter.note}>
                {meter.ceiling > 0 && meter.inUse / meter.ceiling <= 0.8
                  ? portalText("operations", "value_value_free_377539fa", { v0: String(String(room)), v1: String(room === 1 ? portalText("operations", "line_264f39ca") : portalText("operations", "lines_8525aaa8")) })
                  : portalText("operations", "close_to_your_limit_9564a90c")}
              </span>
              <button
                type="button"
                className="btn btn-ghost c-va-meter-action"
                onClick={() => {
                  setQuotaRequestOpen(true);
                }}
              >
                {portalText("operations", "request_more_lines_cba2054b")}</button>
            </div>
            <PendingQuotaRequestLine />
            <QuotaRequestDialog
              open={quotaRequestOpen}
              currentCeiling={organizationConcurrency ?? meter.ceiling}
              onClose={() => {
                setQuotaRequestOpen(false);
              }}
            />
          </div>

          <p className="c-va-label">{portalText("operations", "live_conversations_cca62c52")}</p>
          {listed.length === 0 ? (
            <p className="c-note">
              {portalText("operations", "nothing_is_on_a_call_right_now_this_list_fills_the_moment_60631a2a")}</p>
          ) : (
            <div className="c-va-live-list">
              {listed.map((one) => {
                const row = (
                <button
                  key={one.conversationId}
                  type="button"
                  className="c-va-live-row"
                  aria-current={one.conversationId === selected?.conversationId}
                  onClick={() => {
                    onSelect(one.conversationId);
                  }}
                >
                  <span className="c-va-live-row-head">
                    <span className="c-va-live-row-party">
                      <span className="c-va-live-row-icon">
                        <Icon glyph={MEDIUM_GLYPH[one.medium]} size="lg" />
                      </span>
                      <span className="c-va-live-row-name">{partyLabel(one.partyAddress)}</span>
                    </span>
                    <span className="c-va-live-row-duration">
                      {formatDuration(one.elapsedSeconds)}
                    </span>
                  </span>
                  <span className="c-va-live-row-meta">
                    {one.medium === "web_chat"
                      ? portalText("operations", "web_chat_a100d197")
                      : one.medium === "voice"
                        ? portalText("operations", "history.medium_voice")
                        : one.medium}{" "}
                    ·{" "}
                    {languageName(one.languageCode)}
                    {/* plan-026 (0176): a beta language carries its Beta badge. */}
                    {isBetaLanguage(languagePack.data, one.languageCode) ? (
                      <>
                        {" "}
                        <LanguageBetaTag />
                      </>
                    ) : null}{" "}
                    · {one.phase}
                    {/* plan-021: another VoiceAgent of the workspace is answering it after a pass. */}
                    {nowWithTextOf(one.actingStint, voiceAgentId) === null ? null : (
                      <> · {nowWithTextOf(one.actingStint, voiceAgentId)}</>
                    )}
                  </span>
                </button>
                );
                // WAPP-ENDCALL: an outbound conversation, and a person who may dial — the row carries "End call".
                const outboundOrigin = one.outboundOrigin ?? null;
                return outboundOrigin === null || !mayDial ? (
                  row
                ) : (
                  <div key={one.conversationId} className="c-va-live-item">
                    {row}
                    <EndOutboundConversation
                      conversationId={one.conversationId}
                      outboundOrigin={outboundOrigin}
                      partyText={partyLabel(one.partyAddress)}
                      onEnded={markEnded}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selected === undefined ? null : (
          <LiveTranscript
            key={selected.conversationId}
            conversationId={selected.conversationId}
            partyAddress={selected.partyAddress}
            technicalDetail={technicalDetailOf(selected)}
            callerWord={
              survey && selected.medium === "voice" ? portalText("operations", "history.person_called") : null
            }
            voiceAgentId={voiceAgentId}
          />
        )}
      </div>
    </>
  );
}

/**
 * plan-015 LIVE (G14, design §28.5.14): how the ⓘ words the leg's codec. Keyed by the server's spelling (migration
 * 0127's set) — the console states no codec of its own, so a leg shows the codec it reported or none.
 */
const CODEC_WORD = {
  get pcmu() { return portalText("operations", "g_711_law_5ba5e7f4"); },
  get pcma() { return portalText("operations", "g_711_a_law_1628aa51"); },
  g722: "G.722",
  l16: "L16",
  opus: "Opus",
  amr_wb: "AMR-WB",
} as const satisfies Record<NonNullable<LiveBoardConversationView["mediaCodec"]>, string>;

/**
 * What the ⓘ shows for one conversation in progress (the design's "codec · latency · id"): for a voice leg,
 * "G.722 · 16 kHz · 640 ms · <id>", or "Codec not reported · <id>" when the leg reported none; the latency only once a
 * turn has been measured. A text conversation keeps its medium and id.
 */
export function technicalDetailOf(one: LiveBoardConversationView): string {
  if (one.medium !== "voice") return `${one.medium === "web_chat" ? portalText("operations", "web_chat_a100d197") : one.medium} · ${one.conversationId}`;
  const codec =
    one.mediaCodec === null || one.mediaSampleRateHz === null
      ? portalText("operations", "codec_not_reported_54f943ed")
      : portalText("operations", "value_value_khz_fe9fa3ef", { v0: String(CODEC_WORD[one.mediaCodec]), v1: String(String(one.mediaSampleRateHz / 1000)) });
  const latency = one.latencyMs === null ? [] : [portalText("operations", "value_ms_8eedbde5", { v0: String(String(one.latencyMs)) })];
  return [codec, ...latency, one.conversationId].join(" · ");
}

/** The twelve voice bars of the transcript's head: each its own pace, so they never move in step. */
const VOICE_BAR = Array.from({ length: 12 }, (_, index) => ({
  duration: `${String(0.7 + ((index * 7) % 9) / 10)}s`,
  delay: `${String((index % 5) * 0.11)}s`,
}));

export function LiveTranscript({
  conversationId,
  partyAddress,
  technicalDetail = null,
  callerWord = null,
  voiceAgentId = null,
}: {
  readonly conversationId: string;
  readonly partyAddress: string | null;
  /** What the ⓘ button shows (design sync 2026-10-09); `null` draws no button. */
  readonly technicalDetail?: string | null;
  /** The far end's label when it is not "Caller" (a survey's "Person called"); `null` keeps "Caller". */
  readonly callerWord?: string | null;
  /**
   * plan-029 WAPP-HIST1: the VoiceAgent whose Live board this is — "Block this number" is offered where the
   * conversation's `may_block` is true. `null` (another screen's transcript) offers no block.
   */
  readonly voiceAgentId?: string | null;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const [detailOpen, setDetailOpen] = useState(false);
  const [line, setLine] = useState<readonly TranscriptLineView[]>([]);
  const [partial, setPartial] = useState<TranscriptLineView | null>(null);
  const [ended, setEnded] = useState(false);
  // plan-029 WAPP-HIST1: whether the caller may be blocked (read with the stored turns), and, once it has ended, why —
  // when caller protection ended it ("Ended: the caller said nothing").
  const [mayBlock, setMayBlock] = useState(false);
  const [partyEndReason, setPartyEndReason] = useState<PartyProtectionEndReason | null>(null);
  // plan-029 WAPP-HIST2: caller confirmation's state — waiting for the code, confirmed, could not confirm.
  const [confirmation, setConfirmation] = useState<LiveConfirmationState>(null);

  // Review #89: opened mid-call, the tab first shows what was already said — the stored `turn`
  // rows — then every `turn.final` after them. Joined by turn index, so a line that arrives on
  // the stream while the rows are being read is shown once.
  useEffect(() => {
    let current = true;
    // Through a resolved promise, so a gateway that throws rather than rejects cannot break the tab.
    void Promise.resolve()
      .then(() => gateway.getPastConversation(conversationId))
      .then(
      (past) => {
        if (!current) return;
        setMayBlock(past.mayBlock === true);
        const record = past.partyConfirmation ?? null;
        if (record !== null) setConfirmation((held) => held ?? record.outcome);
        setLine((heard) => {
          const stored = new Set(past.transcript.map((each) => each.turnIndex));
          return [...past.transcript, ...heard.filter((each) => each.turnIndex === undefined || !stored.has(each.turnIndex))];
        });
      },
      () => {
        // Not readable yet (the conversation row is still being written): the stream carries on.
      },
    );
    return () => {
      current = false;
    };
  }, [gateway, conversationId]);

  useEffect(() => {
    const subscription = gateway.streamConversationTranscript(conversationId, (frame) => {
      switch (frame.event) {
        case "turn.partial": {
          const data = readRecord(frame.data, "turn.partial");
          setPartial({
            speaker: speakerOf(readText(data["speaker"], "turn.partial.speaker")),
            text: readText(data["text"], "turn.partial.text"),
            partial: true,
          });
          break;
        }
        case "turn.final": {
          const data = readRecord(frame.data, "turn.final");
          const next: TranscriptLineView = {
            speaker: speakerOf(readText(data["speaker"], "turn.final.speaker")),
            text: readText(data["text"], "turn.final.text"),
            partial: false,
            ...(typeof data["turn_index"] === "number" ? { turnIndex: data["turn_index"] } : {}),
          };
          setPartial(null);
          setLine((current) =>
            next.turnIndex !== undefined && current.some((each) => each.turnIndex === next.turnIndex)
              ? current
              : [...current, next],
          );
          break;
        }
        case "conversation.event": {
          // plan-029 WAPP-HIST2: a caller confirmation event moves Live's state (codes only, never a code).
          const detail = partyConfirmationFrameDetailOf(frame.data);
          if (detail !== null) setConfirmation((held) => liveConfirmationStateAfter(held, detail));
          break;
        }
        case "conversation.ended": {
          setEnded(true);
          // plan-029: read why it ended, once — caller protection's reason is said beside "Ended".
          void Promise.resolve()
            .then(() => gateway.getPastConversation(conversationId))
            .then(
              (past) => {
                setMayBlock(past.mayBlock === true);
                setPartyEndReason(isPartyProtectionEndReason(past.endReason) ? past.endReason : null);
              },
              () => {
                // Not readable yet: "Ended" alone is still true.
              },
            );
          break;
        }
        default:
          break;
      }
    });

    return () => {
      subscription.close();
    };
  }, [gateway, conversationId]);

  const shown = partial === null ? line : [...line, partial];

  return (
    <div className="c-va-card c-va-transcript">
      <div className="c-va-transcript-head">
        <span className={ended ? "c-va-transcript-led c-va-transcript-led-ended" : "c-va-transcript-led"}>
          <span className="c-va-transcript-dot" />
          {ended ? portalText("operations", "ended_d5d26ffd") : portalText("operations", "live_6990f01a")}
        </span>
        {liveConfirmationText(confirmation) === null ? null : (
          <span className="c-va-transcript-meta" data-live-confirmation={confirmation ?? ""}>
            {liveConfirmationText(confirmation)}
          </span>
        )}
        {ended && partyEndReason !== null ? (
          <span className="c-va-transcript-meta">{partyProtectionReasonText(partyEndReason)}</span>
        ) : null}
        <span className="c-va-transcript-party">{partyLabel(partyAddress)}</span>
        {ended ? null : (
          <span className="c-va-transcript-bars" aria-hidden="true">
            {VOICE_BAR.map((bar, index) => (
              <span
                key={String(index)}
                className="c-va-transcript-bar"
                style={{ animationDuration: bar.duration, animationDelay: bar.delay }}
              />
            ))}
          </span>
        )}
        {technicalDetail === null ? null : (
          <>
            <button
              type="button"
              className="btn btn-icon c-va-transcript-info"
              aria-expanded={detailOpen}
              aria-label={portalText("operations", "technical_details_4be37be9")}
              title={technicalDetail}
              onClick={() => {
                setDetailOpen(!detailOpen);
              }}
            >
              <Icon glyph={GLYPH.info} size="md" />
            </button>
            {detailOpen ? <span className="c-va-transcript-meta">{technicalDetail}</span> : null}
          </>
        )}
      </div>
      <div className="c-va-transcript-body">
        {shown.length === 0 ? (
          <p className="c-note">{portalText("operations", "waiting_for_the_first_turn_a3b3838b")}</p>
        ) : (
          shown.map((one, index) => (
            <p className="c-va-turn" key={`${String(index)}-${one.text.slice(0, 16)}`}>
              <span
                className={
                  one.speaker === "assistant" ? "c-va-turn-who c-va-turn-who-agent" : "c-va-turn-who"
                }
              >
                {one.speaker === "assistant" ? portalText("operations", "agent_5ce2e6f4") : one.speaker === "caller" ? (callerWord ?? portalText("operations", "caller_3fda6d67")) : portalText("operations", "system_bc0792d8")}
              </span>
              <span className={one.partial ? "c-va-turn-partial" : undefined}>{one.text}</span>
            </p>
          ))
        )}
      </div>
      {/* plan-029 WAPP-HIST1: offered only where the api says so (a known phone caller, a reader who may block). */}
      {voiceAgentId !== null && mayBlock ? (
        <div className="c-va-history-actions">
          <BlockThisNumber conversationId={conversationId} voiceAgentId={voiceAgentId} />
        </div>
      ) : null}
    </div>
  );
}

/** The transcript speaker labels of C1e. Anything else is a server defect, not a speaker. */
function speakerOf(value: string): TranscriptLineView["speaker"] {
  if (value === "assistant" || value === "caller" || value === "system") return value;
  throw new Error(portalText("operations", "value_is_not_a_transcript_speaker_label_c1e_assistant_call_ecf7c90d", { v0: String(value) }));
}
